import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Capacitor } from "@capacitor/core";
import { pickBackCameraId, barcodeVideoConstraints, tuneFocus, shouldPeriodicRefocus, refocusOnce, readZoom, applyZoom, bigScanBox, scanSuccessFeedback } from "@/lib/scannerCamera";
import { ScanSuccessFlash } from "@/components/ScanSuccessFlash";
import { ScannerZoomControl } from "@/components/ScannerZoomControl";
import { usePinchZoom, isMultiTouch } from "@/lib/usePinchZoom";
const IS_NATIVE = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();
import { toast } from "sonner";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { unitFor } from "@/lib/liquid";
import { trafficLight, LIGHT_STYLES } from "@/lib/nutrientLight";

// Compact sugar / sat-fat / salt traffic lights for result rows (per 100 g).
function NutrientLights({ per100, name, uid, labels }) {
  const isDrink = unitFor(name) === "ml";
  const dots = ["sugar", "saturated_fat", "salt"].map((k) => ({ k, light: trafficLight(k, per100?.[k], isDrink) })).filter((d) => d.light);
  if (!dots.length) return null;
  return (
    <span className="inline-flex items-center gap-1 shrink-0" data-testid={`food-lights-${uid}`}>
      {dots.map((d) => (
        <span
          key={d.k}
          className={`inline-flex h-3.5 min-w-[14px] items-center justify-center rounded-full px-0.5 text-[8px] font-bold leading-none text-white ${LIGHT_STYLES[d.light].dot}`}
          data-light={d.light} data-kind={d.k}
        >
          {labels[d.k]}
        </span>
      ))}
    </span>
  );
}
import { pieceInfoFor, PIECE_FRACTIONS } from "@/lib/pieceWeights";
import {
  Search, ScanLine, Loader2, Camera, X, ChevronLeft, Check,
  Heart, Plus, Minus, Pencil, Trash2, FileText, History, Database,
  UserSquare2, BookOpen, Zap, ZapOff, ChevronRight,
} from "lucide-react";

// 5 sources — rendered as 5 square tile buttons in the header. Each has an
// icon so it reads at a glance without depending on translation length.
const TABS = [
  { key: "recent",    label: "food.tabRecent",  icon: History },
  { key: "db",        label: "food.tabDb",      icon: Database },
  { key: "favorites", label: "food.tabFav",     icon: Heart },
  { key: "custom",    label: "food.tabCustom",  icon: UserSquare2 },
  { key: "recipes",   label: "food.tabRecipes", icon: BookOpen },
];

// Detect whether a per_100g block has micronutrients (=> show DETAIL badge)
const hasDetail = (p) => {
  if (!p) return false;
  return (p.sugar != null) || (p.fiber != null) || (p.saturated_fat != null) || (p.salt != null);
};

// Round half-up to 1 decimal place
const round1 = (x) => Math.round(x * 10 + Number.EPSILON) / 10;

export default function FoodSearchDialog({
  open,
  onOpenChange,
  onSelect,
  defaultTab = "search",           // legacy: "search" | "barcode"
  autoStartScanner = false,
  initialQuery = "",               // pre-fill the search field when the dialog opens
  initialBarcode = "",             // open directly on this EAN (skips the camera)
  initialQuantityG = null,         // pre-fill grams (used by voice quick-search when the user says "150g")
}) {
  const { t, lang } = useT();

  // Which source tab is active — default to "db" (Solano-Datenbank) so users can find any food immediately.
  const [tab, setTab] = useState("db");
  // Search query
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [favIds, setFavIds] = useState(new Set()); // set of favorite keys (barcode/name+source)
  const [favMap, setFavMap] = useState({}); // key -> favorite_id (for remove)

  // Barcode scanner state
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const [barcode, setBarcode] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  // Zoom control state. Populated when the live camera track exposes a
  // `zoom` capability — the small +/- control on the scanner overlay
  // reads/writes `zoom.value`. When the capability isn't exposed (some
  // iPhones, most Firefox builds) `zoom` stays null and the UI hides
  // the control automatically.
  const [zoom, setZoom] = useState(null);   // { min, max, step, value } | null
  const [scanHit, setScanHit] = useState(false);
  const scannerRef = useRef(null);
  const stableCodeRef = useRef({ code: null, hits: 0, locked: false });
  const scannerId = "barcode-scanner-region";
  // Interval handle for the periodic macro-refocus loop. Some phones —
  // especially older Samsung/Oppo and iPhones without a dedicated macro
  // lens — silently lose focus after a couple of seconds when the user
  // holds the phone 5-10cm from the pack. We kick a single-shot AF every
  // 2s so the focus never stays stuck on far distance. Cleared on stop.
  const _refocusIntervalRef = useRef(null);

  // Find the live MediaStreamTrack rendered inside the scanner region so we
  // can toggle the phone's flashlight ("torch") when the user is in a dark
  // room. Torch support depends on both the OS and the browser — Chrome
  // Android usually has it, iOS Safari currently does NOT.
  const _videoTrack = () => {
    const el = document.getElementById(scannerId);
    const video = el?.querySelector("video");
    const stream = video?.srcObject;
    return stream?.getVideoTracks?.()[0] || null;
  };
  const toggleTorch = async () => {
    const track = _videoTrack();
    if (!track) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch { /* torch not supported on this device */ }
  };

  // Tap-to-focus — user taps a spot on the live camera feed, we ask the
  // browser to refocus there. We also render a short-lived visual ripple
  // so the tap feels responsive even on devices where the hardware focus
  // change is invisible.
  const [focusRipple, setFocusRipple] = useState(null); // { x, y, key } in %
  const focusAt = async (evt) => {
    if (isMultiTouch(evt)) return;
    const el = document.getElementById(scannerId);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clientX = evt.clientX ?? evt.touches?.[0]?.clientX;
    const clientY = evt.clientY ?? evt.touches?.[0]?.clientY;
    if (clientX == null) return;
    const relX = (clientX - rect.left) / rect.width;
    const relY = (clientY - rect.top) / rect.height;
    setFocusRipple({ x: relX * 100, y: relY * 100, key: Date.now() });
    const track = _videoTrack();
    if (!track) return;
    await refocusOnce(track, { x: relX, y: relY });
    setTimeout(() => setFocusRipple(null), 800);
  };

  // Photo of the product taken while scanning — carried into the Custom
  // Product form on 1-Tap Add so users can visually identify the product
  // when they see it again in their food-log history. Kept as a data URL
  // in local state; the backend saves it to /uploads/... and stores only
  // the URL in Mongo.
  const [scannedProductPhoto, setScannedProductPhoto] = useState(null);

  // Grab the current camera frame as a downscaled JPEG data URL. We call
  // this from `onFound` BEFORE stopping the scanner so the video element
  // is still live. Errors (no video track, canvas taint) fail silently —
  // the photo is a nice-to-have, not a hard requirement of the 1-Tap Add.
  const _captureVideoFrame = useCallback(() => {
    try {
      const el = document.getElementById(scannerId);
      const video = el?.querySelector("video");
      if (!video || !video.videoWidth || !video.videoHeight) return null;
      const maxSize = 640;
      const scale = Math.min(1, maxSize / Math.max(video.videoWidth, video.videoHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/jpeg", 0.75);
    } catch {
      return null;
    }
  }, []);

  // Manual add-product form (shown when a scanned/typed barcode is not found)
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    barcode: "",
    name: "",
    brand: "",
    calories: "",
    protein: "",
    carbs: "",
    fat: "",
    sugar: "",
    fiber: "",
    saturated_fat: "",
    salt: "",
    photo_base64: null,        // captured during 1-Tap Add
  });
  const [manualSaving, setManualSaving] = useState(false);

  // Portion selection step
  const [selectedFood, setSelectedFood] = useState(null);
  const [quantityG, setQuantityG] = useState(100);
  const [portionMode, setPortionMode] = useState("100g"); // "100g" | "portion"

  // Custom-food form state
  const [customForm, setCustomForm] = useState(null); // null | { editing_id?, name, brand, notes, per_100g:{...} }

  const errorMap = {
    permission: { i18n: "food.camPermission", withTip: true },
    iframe: { i18n: "food.camIframe", withTip: true },
    nodevice: { i18n: "food.camNoDevice", withTip: false },
    busy: { i18n: "food.camBusy", withTip: false },
    generic: { i18n: "food.camError", withTip: false },
  };

  // ---------- Favorites helpers ----------
  const favKeyOf = useCallback((f) => {
    if (f.barcode) return `bc:${f.barcode}`;
    if (f.recipe_id) return `re:${f.recipe_id}`;
    return `nm:${(f.source || tab)}:${(f.name || "").toLowerCase()}`;
  }, [tab]);

  const reloadFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/foods/favorites");
      const ids = new Set();
      const map = {};
      (data || []).forEach((f) => {
        const key = f.barcode ? `bc:${f.barcode}`
                : f.recipe_id ? `re:${f.recipe_id}`
                : `nm:${f.source || "db"}:${(f.name || "").toLowerCase()}`;
        ids.add(key);
        map[key] = f.favorite_id;
      });
      setFavIds(ids);
      setFavMap(map);
    } catch { /* ignore */ }
  }, []);

  // ---------- Scanner ----------
  const stopScanner = useCallback(async () => {
    if (_refocusIntervalRef.current) {
      clearInterval(_refocusIntervalRef.current);
      _refocusIntervalRef.current = null;
    }
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) await scannerRef.current.stop();
        scannerRef.current.clear();
      } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScannerActive(false);
    setTorchOn(false);
    setTorchSupported(false);
    setZoom(null);
  }, []);

  // Slider/−/+ driven zoom (ScannerZoomControl).
  const setZoomValue = async (next) => {
    if (!zoom) return;
    const track = _videoTrack();
    if (await applyZoom(track, next)) setZoom({ ...zoom, value: next });
  };

  const doBarcodeCode = useCallback(async (code, capturedPhoto = null) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/foods/barcode/${code.trim()}`);
      setBarcodeOpen(false);
      setTab("db");
      setResults([data]);
      setQ(data.name || code);
      // Jump straight to the portion step with the exact product. Leaving the
      // user on the list would let the name-search effect (triggered by setQ)
      // replace the exact barcode hit with unrelated generic results.
      setSelectedFood(data);
      let g = 100;
      const ss = String(data.serving_size || "").match(/(\d+(?:[.,]\d+)?)\s*g/i);
      if (ss) {
        const parsed = parseFloat(ss[1].replace(",", "."));
        if (parsed > 0 && parsed <= 2000) g = parsed;
      }
      setQuantityG(g);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        // Product missing from OpenFoodFacts → close the scanner overlay
        // and immediately open the manual add-product form, pre-filled
        // with the scanned code + the photo we captured a moment earlier
        // from the live camera feed. That photo helps the user recognize
        // this same product next time they open their food-log history.
        await stopScanner();
        setBarcodeOpen(false);
        const photo = capturedPhoto || scannedProductPhoto || null;
        setManualForm((f) => ({ ...f, barcode: code.trim(), name: "", photo_base64: photo }));
        setManualOpen(true);
        toast.info(t("food.barcodeNotFoundAddManually") || t("food.notFound"));
      } else {
        toast.error(e.response?.data?.detail || t("food.notFound"));
      }
    } finally { setLoading(false); }
  }, [t, stopScanner, scannedProductPhoto]);

  // Photograph a nutrition label directly (fallback when the barcode can't
  // be scanned or the pack has no barcode). We resize the image, run it
  // through the existing GPT-5.2 label-OCR endpoint, and — on success —
  // route the returned per-100g values through the same pick/confirm-
  // quantity flow as an OpenFoodFacts barcode hit. Users end up in the
  // familiar "how many grams did you eat?" screen with the food name
  // pre-filled.
  const [labelLoading, setLabelLoading] = useState(false);
  const _resizeLabelImg = useCallback((file, maxSize = 1400, quality = 0.8) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = url;
  }), []);
  const handleLabelFile = useCallback(async (file) => {
    if (!file) return;
    setLabelLoading(true);
    try {
      const dataUrl = await _resizeLabelImg(file);
      const { data } = await api.post("/foods/nutrition-label/analyze", { image_base64: dataUrl });
      const p100 = data?.per_100g || {};
      // Require at least kcal + one macro to consider the read a success.
      const kcal = p100.calories;
      const hasMacros = ["protein", "carbs", "fat"].some(
        (k) => p100[k] != null && p100[k] > 0,
      );
      if (kcal == null && !hasMacros) {
        toast.error(t("food.labelUnreadable") || "Etichetta illeggibile, riprova con più luce");
        return;
      }
      // Build a synthetic food record matching the OpenFoodFacts shape and
      // hand it to `pick()` so the user reviews grams before saving.
      const name = data.name || data.brand || t("food.labelProduct") || "Prodotto da etichetta";
      const code = (barcode || "").trim();
      const food = {
        source: "label_ocr",
        name,
        brand: data.brand || null,
        barcode: code || null,
        per_100g: p100,
        image_url: null,
      };
      // If the user typed/scanned an EAN before falling back to the label
      // photo, persist the mapping so the next scan of this code resolves
      // directly to this product (best-effort).
      if (code) {
        api.post("/foods/barcode", {
          barcode: code, name, brand: data.brand || null,
          per_100g: p100, photo_base64: null,
        }).catch(() => {});
      }
      await stopScanner();
      setBarcodeOpen(false);
      setSelectedFood(food);
      // Default portion: parse "30 g" out of `serving_size` if the label
      // showed a portion; else 100 g.
      let g = 100;
      const ss = String(data.serving_size || "").match(/(\d+(?:[.,]\d+)?)/);
      if (ss) {
        const parsed = parseFloat(ss[1].replace(",", "."));
        if (parsed > 0 && parsed <= 2000) g = parsed;
      }
      setQuantityG(g);
      if (data.warning) toast.warning(data.warning);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("food.labelError") || "Errore lettura etichetta");
    } finally {
      setLabelLoading(false);
    }
  }, [_resizeLabelImg, t, barcode]);

  const saveManualBarcode = useCallback(async () => {
    const f = manualForm;
    if (!f.barcode.trim() || !f.name.trim()) {
      toast.error(t("food.manualAdd.needBasics"));
      return;
    }
    setManualSaving(true);
    try {
      const per100 = {};
      for (const k of ["calories", "protein", "carbs", "fat", "sugar", "fiber", "saturated_fat", "salt"]) {
        const v = f[k];
        if (v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v))) {
          per100[k] = Number(v);
        }
      }
      const { data } = await api.post("/foods/barcode", {
        barcode: f.barcode.trim(),
        name: f.name.trim(),
        brand: f.brand.trim() || null,
        per_100g: per100,
        photo_base64: f.photo_base64 || null,
      });
      setManualOpen(false);
      setBarcodeOpen(false);
      setTab("db");
      setResults([data]);
      setQ(data.name || f.barcode);
      // Straight to the portion step (same reason as in doBarcodeCode).
      setSelectedFood(data);
      setQuantityG(100);
      setScannedProductPhoto(null);
      toast.success(t("food.manualAdd.saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally {
      setManualSaving(false);
    }
  }, [manualForm, t]);

  const startScanner = useCallback(async () => {
    stableCodeRef.current = { code: null, hits: 0, locked: false };
    setScannerError(null);
    setScannerActive(true);
    await new Promise((r) => setTimeout(r, 250));

    let testStream = null;
    try {
      testStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
    } catch (e1) {
      if (e1?.name === "OverconstrainedError" || e1?.name === "ConstraintNotSatisfiedError") {
        try {
          testStream = await navigator.mediaDevices.getUserMedia({ video: true });
        } catch (e2) {
          const code = e2?.name === "NotAllowedError" ? "permission"
                     : e2?.name === "NotFoundError" ? "nodevice"
                     : e2?.name === "NotReadableError" ? "busy"
                     : window.self !== window.top ? "iframe" : "generic";
          setScannerError({ code });
          setScannerActive(false);
          return;
        }
      } else {
        const code = e1?.name === "NotAllowedError" ? "permission"
                   : e1?.name === "NotFoundError" ? "nodevice"
                   : e1?.name === "NotReadableError" ? "busy"
                   : window.self !== window.top ? "iframe" : "generic";
        setScannerError({ code });
        setScannerActive(false);
        return;
      }
    }
    if (testStream) testStream.getTracks().forEach((tr) => tr.stop());

    try {
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
      // ONLY retail formats with a check digit (EAN/UPC). CODE_128/39/ITF have
      // no (or weak) checksums — a blurry cheese label could decode as a random
      // valid-looking number and pull up a completely different product.
      const supportedFormats = [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
      ];
      const scanner = new Html5Qrcode(scannerId, {
        formatsToSupport: supportedFormats,
        // The single biggest scanner-quality win: use the browser's native
        // BarcodeDetector where it's available (Android Chrome, iOS 17+,
        // desktop Chrome/Edge). Native detection is 5-10× faster and much
        // more forgiving of angle / lighting / motion blur than the pure
        // JS fallback (ZXing).
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        useBarCodeDetectorIfSupported: true,
        verbose: false,
      });
      scannerRef.current = scanner;

      const backCamId = await pickBackCameraId(Html5Qrcode);
      const startConfig = {
        fps: 15,
        // Scan window ≈ full frame: users no longer have to "hunt" the small box.
        qrbox: bigScanBox,
        aspectRatio: 1.777,          // 16:9 → higher effective pixel width
        disableFlip: false,          // some products have mirrored labels
        videoConstraints: barcodeVideoConstraints(backCamId),
      };

      const onFound = async (code) => {
        // Stability gate: require the SAME code in two consecutive decodes
        // before accepting — filters out single-frame misreads that would
        // otherwise fetch the wrong product.
        const st = stableCodeRef.current;
        if (st.code === code) {
          st.hits += 1;
        } else {
          stableCodeRef.current = { code, hits: 1 };
          return;
        }
        if (st.hits < 2 || st.locked) return;
        st.locked = true;
        // Capture the current camera frame BEFORE stopping the scanner —
        // once the MediaStream is released the video element goes blank
        // and we lose our chance to save the product's visual. The 1-Tap
        // Add flow will attach this photo to the Custom Product form if
        // the EAN is missing from OpenFoodFacts.
        const frame = _captureVideoFrame();
        setScannedProductPhoto(frame);
        // Instant feedback: green frame + haptic, visible ~450 ms before the view switches.
        setScanHit(true);
        scanSuccessFeedback();
        await new Promise((r) => setTimeout(r, 450));
        await stopScanner();
        setScanHit(false);
        setBarcode(code);
        await doBarcodeCode(code, frame);
      };

      try {
        await scanner.start({ facingMode: "environment" }, startConfig, onFound, () => {});
      } catch (envErr) {
        const cams = await Html5Qrcode.getCameras();
        if (cams && cams.length > 0) {
          // videoConstraints override the camera id → point them at the fallback device.
          startConfig.videoConstraints = barcodeVideoConstraints(cams[cams.length - 1].id);
          await scanner.start(cams[cams.length - 1].id, startConfig, onFound, () => {});
        } else {
          throw envErr;
        }
      }
      // After the stream is live, probe track capabilities to squeeze
      // every last percent of scan reliability out of the phone:
      //
      // 1. **Focus distance → minimum** (macro). This is the biggest fix
      //    for the Feb-2026 beta report "some testers can't focus close
      //    on the EAN". Continuous AF alone often locks on far distance.
      // 2. **Zoom → 2× (or the smallest supported step above 1×)** so
      //    even tiny EAN codes on 500 ml bottles get enough pixels-per-bar
      //    for the decoder — makes a huge difference on entry-level
      //    Android sensors.
      // 3. **Periodic single-shot AF** every 2 s. Even with `continuous`
      //    mode, some phones drift once the pack is stationary; this
      //    keeps AF alive without needing the user to tap.
      // 4. **Torch capability probe** so the flashlight button shows
      //    only when it'd actually work.
      setTimeout(async () => {
        const track = _videoTrack();
        if (!track) return;
        const caps = track.getCapabilities?.() || {};
        setTorchSupported(!!caps.torch);
        setTorchOn(false);

        // ---- Autofocus (continuous; macro distance only off-Android) ----
        await tuneFocus(track);

        // ---- Digital zoom (2× or nearest step) + expose range to the slider ----
        const zr = readZoom(track);
        if (zr) {
          const target = Math.min(2, zr.max);
          if (target > zr.min && (await applyZoom(track, target))) zr.value = target;
          setZoom(zr);
        } else {
          setZoom(null);
        }

        // ---- Periodic re-focus loop (not on Android: it disables continuous AF) ----
        if (_refocusIntervalRef.current) clearInterval(_refocusIntervalRef.current);
        if (shouldPeriodicRefocus()) {
          _refocusIntervalRef.current = setInterval(async () => {
            const t = _videoTrack();
            if (!t) return;
            try {
              await t.applyConstraints({ advanced: [{ focusMode: "single-shot" }] });
            } catch { /* noop */ }
          }, 2000);
        }
      }, 400);
    } catch (e) {
      setScannerError({ code: "generic" });
      setScannerActive(false);
    }
  }, [stopScanner, doBarcodeCode, _captureVideoFrame]);

  // ---------- Reset on open/close ----------
  useEffect(() => {
    if (open) {
      setTab("db");
      setQ(initialQuery || "");
      setResults([]);
      setSelectedFood(null);
      setQuantityG(initialQuantityG && initialQuantityG > 0 ? initialQuantityG : 100);
      setPortionMode("100g");
      setCustomForm(null);
      setBarcodeOpen(defaultTab === "barcode");
      reloadFavorites();
      if (initialBarcode) {
        doBarcodeCode(initialBarcode);
      } else if (autoStartScanner && defaultTab === "barcode") {
        startScanner();
      }
    } else {
      stopScanner();
      setBarcodeOpen(false);
      setScannerError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---------- Load list when tab or query changes ----------
  useEffect(() => {
    if (!open) return;
    if (selectedFood) return; // don't reload during portion step
    const isDb = tab === "db";
    // For DB: need >=2 chars. For other tabs: load all
    if (isDb && q.trim().length < 2) {
      setResults([]);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams();
    params.set("q", q);
    params.set("source", tab);
    if (lang) params.set("lang", lang);
    api.get(`/foods/search?${params.toString()}`, { signal: controller.signal })
      .then(({ data }) => setResults(data || []))
      .catch((err) => {
        if (err.code === "ERR_CANCELED") return;
        setResults([]);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [tab, q, open, selectedFood, lang]);

  // ---------- Selection & confirm ----------
  const pick = (food) => {
    setSelectedFood(food);
    // Priority for the pre-filled portion:
    //   1) Voice quick-search may have parsed a grams value → keep it.
    //   2) History items (Cronologia): use the LAST portion the user logged
    //      (e.g. 50g) so re-logging doesn't silently default back to 100g.
    //   3) Recipes with a portion_g → default to one serving.
    //   4) Fallback: 100g.
    if (initialQuantityG && initialQuantityG > 0) {
      setQuantityG(initialQuantityG);
    } else if (food.source === "recent" && food.portion_g) {
      setQuantityG(food.portion_g);
    } else if (food.source === "recipe" && food.portion_g) {
      setQuantityG(food.portion_g);
    } else {
      setQuantityG(100);
    }
  };

  // Healthier swaps (only offered when the chosen product has a red light)
  const [altState, setAltState] = useState({ items: null, loading: false, forId: null });
  const redKinds = useMemo(() => {
    const p = selectedFood?.per_100g || {};
    const drink = unitFor(selectedFood?.name) === "ml";
    return ["sugar", "saturated_fat", "salt"].filter((k) => trafficLight(k, p[k], drink) === "red");
  }, [selectedFood]);
  const loadAlternatives = async () => {
    if (!selectedFood) return;
    setAltState({ items: null, loading: true, forId: selectedFood.id || selectedFood.name });
    try {
      const { data } = await api.get("/foods/alternatives", { params: { name: selectedFood.name, food_id: selectedFood.id || selectedFood.food_id || undefined, lang } });
      setAltState({ items: data.items || [], loading: false, forId: selectedFood.id || selectedFood.name });
    } catch {
      setAltState({ items: [], loading: false, forId: selectedFood.id || selectedFood.name });
    }
  };
  const altVisible = altState.forId === (selectedFood?.id || selectedFood?.name);

  const confirmQuantity = () => {
    if (!selectedFood) return;
    const grams = Math.max(1, Math.min(2000, +quantityG || 100));
    const p100 = selectedFood.per_100g || {};
    const factor = grams / 100;
    onSelect({
      description: `${selectedFood.name}${selectedFood.brand ? ` (${selectedFood.brand})` : ""} · ${grams}${unitFor(selectedFood.name)}`,
      calories: p100.calories ? Math.round(p100.calories * factor) : null,
      protein: p100.protein != null ? round1(p100.protein * factor) : null,
      carbs: p100.carbs != null ? round1(p100.carbs * factor) : null,
      fat: p100.fat != null ? round1(p100.fat * factor) : null,
      sugar: p100.sugar != null ? round1(p100.sugar * factor) : null,
      fiber: p100.fiber != null ? round1(p100.fiber * factor) : null,
      saturated_fat: p100.saturated_fat != null ? round1(p100.saturated_fat * factor) : null,
      salt: p100.salt != null ? round1(p100.salt * factor * 10) / 10 : null,
      food_ref: {
        id: selectedFood.id || selectedFood.food_id || null,
        name: selectedFood.name || null,
        barcode: selectedFood.barcode,
        per_100g: p100,
        quantity_g: grams,
        recipe_id: selectedFood.recipe_id,
        source: selectedFood.source,
      },
    });
    onOpenChange(false);
  };

  const scaled = useMemo(() => {
    if (!selectedFood) return null;
    const p100 = selectedFood.per_100g || {};
    const grams = Math.max(1, +quantityG || 0);
    const factor = grams / 100;
    return {
      kcal: p100.calories != null ? Math.round(p100.calories * factor) : null,
      protein: p100.protein != null ? round1(p100.protein * factor) : null,
      carbs: p100.carbs != null ? round1(p100.carbs * factor) : null,
      fat: p100.fat != null ? round1(p100.fat * factor) : null,
      sugar: p100.sugar != null ? round1(p100.sugar * factor) : null,
      fiber: p100.fiber != null ? round1(p100.fiber * factor) : null,
      saturated_fat: p100.saturated_fat != null ? round1(p100.saturated_fat * factor) : null,
      salt: p100.salt != null ? Math.round(p100.salt * factor * 100) / 100 : null,
    };
  }, [selectedFood, quantityG]);

  // ---------- Favorites toggle ----------
  const toggleFavorite = async (f, ev) => {
    ev?.stopPropagation();
    const key = favKeyOf(f);
    if (favIds.has(key)) {
      const favId = favMap[key];
      if (!favId) return;
      try {
        await api.delete(`/foods/favorites/${favId}`);
        const ids = new Set(favIds); ids.delete(key);
        const map = { ...favMap }; delete map[key];
        setFavIds(ids); setFavMap(map);
        toast.success(t("food.favRemoved"));
        if (tab === "favorites") setResults((r) => r.filter((x) => favKeyOf(x) !== key));
      } catch { toast.error(t("common.error")); }
    } else {
      try {
        const { data } = await api.post("/foods/favorites", {
          food_id: f.id || null,
          source: f.source || "db",
          name: f.name,
          brand: f.brand || null,
          barcode: f.barcode || null,
          image_url: f.image_url || f.image || null,
          recipe_id: f.recipe_id || null,
          per_100g: f.per_100g || {},
        });
        const ids = new Set(favIds); ids.add(key);
        const map = { ...favMap }; map[key] = data.favorite_id;
        setFavIds(ids); setFavMap(map);
        toast.success(t("food.favAdded"));
      } catch { toast.error(t("common.error")); }
    }
  };

  // ---------- Custom food form ----------
  const openCustomCreate = () => setCustomForm({
    editing_id: null, name: "", brand: "", notes: "",
    per_100g: { calories: "", protein: "", carbs: "", fat: "", sugar: "", fiber: "", saturated_fat: "", salt: "" }
  });
  const openCustomEdit = (f, ev) => {
    ev?.stopPropagation();
    setCustomForm({
      editing_id: f.food_id,
      name: f.name || "",
      brand: f.brand || "",
      notes: f.notes || "",
      per_100g: {
        calories: f.per_100g?.calories ?? "",
        protein: f.per_100g?.protein ?? "",
        carbs: f.per_100g?.carbs ?? "",
        fat: f.per_100g?.fat ?? "",
        sugar: f.per_100g?.sugar ?? "",
        fiber: f.per_100g?.fiber ?? "",
        saturated_fat: f.per_100g?.saturated_fat ?? "",
        salt: f.per_100g?.salt ?? "",
      },
    });
  };
  const saveCustomForm = async () => {
    if (!customForm) return;
    if (!customForm.name || customForm.name.trim().length < 2) {
      toast.error(t("food.customName")); return;
    }
    const per_100g = {};
    Object.entries(customForm.per_100g).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) per_100g[k] = +v;
    });
    const payload = {
      name: customForm.name.trim(),
      brand: customForm.brand || null,
      notes: customForm.notes || null,
      per_100g,
    };
    try {
      if (customForm.editing_id) {
        await api.put(`/foods/custom/${customForm.editing_id}`, payload);
      } else {
        await api.post("/foods/custom", payload);
      }
      toast.success(t("food.customCreated"));
      setCustomForm(null);
      // trigger reload
      setResults([]); setQ((q) => q); // no-op to keep query
      const { data } = await api.get(`/foods/search?source=custom&q=${encodeURIComponent(q)}`);
      setResults(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || t("common.error")); }
  };
  const deleteCustom = async (f, ev) => {
    ev?.stopPropagation();
    if (!window.confirm(t("food.deleteConfirm"))) return;
    try {
      await api.delete(`/foods/custom/${f.food_id || f.id}`);
      toast.success(t("food.customDeleted"));
      setResults((r) => r.filter((x) => (x.food_id || x.id) !== (f.food_id || f.id)));
    } catch { toast.error(t("common.error")); }
  };

  // ---------- Render helpers ----------
  const renderMacroLine = (f) => {
    const p = f.per_100g || {};
    // History items: always show the LAST portion the user actually logged
    // (the point of Cronologia is to see what you really ate, not per-100g).
    // Recipes with a portion_g respect the per-100g / per-portion toggle.
    const isRecent = f.source === "recent";
    const useRealPortion = isRecent && f.portion_g;
    const factor = useRealPortion
      ? (f.portion_g / 100)
      : (portionMode === "portion" && f.portion_g ? f.portion_g / 100 : 1);
    const k = p.carbs != null ? Math.round(p.carbs * factor) : "—";
    const e = p.protein != null ? Math.round(p.protein * factor) : "—";
    const g = p.fat != null ? Math.round(p.fat * factor) : "—";
    const kcal = p.calories != null ? Math.round(p.calories * factor) : "—";
    const suffix = useRealPortion
      ? ` / ${f.portion_g}g`
      : (portionMode === "portion" && f.portion_g ? ` / ${f.portion_g}g` : " / 100g");
    return (
      <span className="text-[13px] text-slate-600">
        <span className="font-medium">{k}</span> K · <span className="font-medium">{e}</span> E · <span className="font-medium">{g}</span> F · <span className="font-medium">{kcal}</span> kcal
        <span className="text-slate-400">{suffix}</span>
      </span>
    );
  };

  const emptyForTab = tab === "favorites" ? t("food.noFavYet")
                    : tab === "custom" ? t("food.noCustomYet")
                    : tab === "recipes" ? t("food.noRecipesYet")
                    : tab === "recent" ? t("food.noRecentYet")
                    : t("food.minLettersHint");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 overflow-hidden h-[92vh] sm:h-auto sm:max-h-[85vh] flex flex-col bg-[#E5F0F1] dark:bg-[#0f1416]"
        style={{ paddingTop: "max(0px, calc(env(safe-area-inset-top, 0px) - 4vh))" }}
        data-testid="food-search-dialog"
      >
        <DialogHeader className="px-5 pt-6 pb-3 bg-[#E5F0F1] dark:bg-[#0f1416]">
          <DialogTitle className="font-heading text-xl font-semibold text-[#2C5254] dark:text-teal-200 tracking-tight">
            {t("food.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("food.description")}</DialogDescription>
        </DialogHeader>

        {selectedFood ? (
          <QuantityStep
            t={t}
            selectedFood={selectedFood}
            quantityG={quantityG}
            setQuantityG={setQuantityG}
            scaled={scaled}
            alt={{ redKinds, altVisible, altState, loadAlternatives, onPick: (a) => { pick(a); setAltState({ items: null, loading: false, forId: null }); } }}
            onBack={() => setSelectedFood(null)}
            onConfirm={confirmQuantity}
            onRename={(name) => {
              const f = selectedFood;
              setSelectedFood((cur) => (cur ? { ...cur, name } : cur));
              // Persist the correction for barcode products: future scans of
              // the same EAN resolve locally with the fixed name.
              if (f?.barcode) {
                api.post("/foods/barcode", {
                  barcode: f.barcode, name,
                  brand: f.brand || null,
                  serving_size: f.serving_size || null,
                  per_100g: f.per_100g || {},
                }).then(() => toast.success(t("food.nameSaved"))).catch(() => {});
              }
            }}
          />
        ) : customForm ? (
          <CustomForm
            t={t}
            form={customForm}
            setForm={setCustomForm}
            onSave={saveCustomForm}
            onCancel={() => setCustomForm(null)}
          />
        ) : (
          <>
            {/* Tabs — 5 square tiles with icon + label. Active tile is
                filled teal so the current source is immediately obvious;
                inactive tiles stay white on the ice-blue backdrop. Grid
                lays out in a single row on ≥sm and stays scrollable on
                narrow phones without cramming five underlines together. */}
            <div className="px-4 pt-2 pb-3 bg-[#E5F0F1] dark:bg-[#0f1416]">
              <div className="grid grid-cols-5 gap-2" data-testid="food-tab-tiles">
                {TABS.map((tt) => {
                  const Icon = tt.icon;
                  const active = tab === tt.key;
                  return (
                    <button
                      key={tt.key}
                      type="button"
                      onClick={() => setTab(tt.key)}
                      data-testid={`food-tab-${tt.key}`}
                      className={`aspect-square rounded-2xl flex flex-col items-center justify-center gap-1.5 p-1 transition active:scale-95 shadow-sm ${
                        active
                          ? "bg-[#2C5254] text-white"
                          : "bg-white dark:bg-[#1a1d1e] text-[#2C5254] dark:text-teal-200 hover:shadow-md"
                      }`}
                      aria-pressed={active}
                    >
                      <Icon className={`h-5 w-5 ${active ? "text-white" : "text-[#5A8B8F] dark:text-teal-300"}`} strokeWidth={2} />
                      <span className="text-[10.5px] font-semibold leading-tight text-center px-0.5">
                        {t(tt.label)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Search bar + portion toggle — white pill card on ice-blue,
                matching the AddMeal meal-type / date pills. */}
            <div className="px-5 pt-4 pb-3 space-y-3 bg-[#E5F0F1] dark:bg-[#0f1416]">
              <div className="relative rounded-2xl bg-white dark:bg-[#1a1d1e] shadow-sm">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="food-search-input"
                  placeholder={tab === "db" ? t("food.searchPh") : t("food.searchPh")}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="h-12 pl-11 pr-12 rounded-2xl bg-transparent border-0 shadow-none text-sm text-[#1A1D1E] dark:text-white focus-visible:ring-0"
                />
                {tab === "db" && (
                  <button
                    type="button"
                    onClick={() => { setBarcodeOpen(true); setScannerError(null); }}
                    data-testid="barcode-open"
                    aria-label="barcode"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-xl flex items-center justify-center bg-[#E5F0F1] hover:bg-[#d5e5e7] active:scale-95 transition"
                  >
                    <ScanLine className="h-5 w-5 text-[#2C5254]" />
                  </button>
                )}
              </div>

              <div className="flex items-center justify-between">
                {/* Per 100g / per portion — segmented pill on white bg */}
                <div className="inline-flex rounded-full bg-white dark:bg-[#1a1d1e] shadow-sm p-1 text-xs">
                  <button
                    type="button"
                    onClick={() => setPortionMode("100g")}
                    className={`px-3 py-1.5 rounded-full transition font-medium ${
                      portionMode === "100g"
                        ? "bg-[#2C5254] text-white shadow-sm"
                        : "text-slate-500 hover:text-[#2C5254]"
                    }`}
                    data-testid="portion-toggle-100g"
                  >
                    {t("food.per100g")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPortionMode("portion")}
                    className={`px-3 py-1.5 rounded-full transition font-medium ${
                      portionMode === "portion"
                        ? "bg-[#2C5254] text-white shadow-sm"
                        : "text-slate-500 hover:text-[#2C5254]"
                    }`}
                    data-testid="portion-toggle-portion"
                  >
                    {t("food.perPortion")}
                  </button>
                </div>

                {tab === "custom" && (
                  <Button
                    onClick={openCustomCreate}
                    data-testid="custom-new-btn"
                    size="sm"
                    className="rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white h-9 px-4 gap-1 shadow-sm"
                  >
                    <Plus className="h-4 w-4" /> {t("food.createCustom")}
                  </Button>
                )}
              </div>
            </div>

            {/* Results list — white cards on ice-blue background */}
            <div className="flex-1 overflow-y-auto px-4 pb-4 pt-1 bg-[#E5F0F1] dark:bg-[#0f1416]">
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              )}
              {!loading && results.length === 0 && (
                <div className="py-10 px-6 text-center">
                  <p className="text-sm text-slate-500 dark:text-slate-400">{emptyForTab}</p>
                  {tab !== "db" && q.trim().length >= 2 && (
                    <button
                      type="button"
                      onClick={() => setTab("db")}
                      data-testid="search-in-db-cta"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#2C5254] bg-white hover:bg-white/80 shadow-sm rounded-full px-4 py-2 transition"
                    >
                      <Search className="h-4 w-4" />
                      {t("food.searchInDbCta", { q: q.trim() })}
                    </button>
                  )}
                </div>
              )}
              {!loading && results.map((f) => {
                const key = favKeyOf(f);
                const isFav = favIds.has(key);
                const showDetail = hasDetail(f.per_100g);
                const uid = f.barcode || f.food_id || f.recipe_id || f.id || f.name;
                return (
                  <div
                    key={uid}
                    className="group flex items-center gap-2 rounded-2xl px-3 py-3 mb-2 bg-white dark:bg-[#1a1d1e] shadow-sm hover:shadow-md transition"
                  >
                    <button
                      type="button"
                      onClick={() => pick(f)}
                      data-testid={`food-pick-${uid}`}
                      className="flex-1 min-w-0 text-left"
                    >
                      <p className="font-medium text-slate-800 truncate">
                        {f.name}
                        {(f.brand) && (
                          <span className="font-normal text-slate-500"> ({f.brand})</span>
                        )}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {renderMacroLine(f)}
                        <NutrientLights per100={f.per_100g} name={f.name} uid={uid} labels={{ sugar: t("food.lightSugarShort"), saturated_fat: t("food.lightFatShort"), salt: t("food.lightSaltShort") }} />
                      </div>
                    </button>

                    {showDetail && (
                      <span
                        className="hidden sm:inline-flex text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-[#5A8B8F]/15 text-[#2C5254] uppercase"
                        data-testid={`detail-badge-${uid}`}
                      >
                        {t("food.detailBadge")}
                      </span>
                    )}

                    {f.source === "ai" && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase"
                        title={f.notes || t("food.aiEstimateTip")}
                        data-testid={`ai-badge-${uid}`}
                      >
                        <span aria-hidden>✦</span>{t("food.aiBadge")}
                      </span>
                    )}

                    {tab === "custom" ? (
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={(e) => openCustomEdit(f, e)}
                          data-testid={`custom-edit-${uid}`}
                          className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-200"
                          aria-label="edit"
                        >
                          <Pencil className="h-4 w-4 text-slate-500" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => deleteCustom(f, e)}
                          data-testid={`custom-delete-${uid}`}
                          className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-red-50"
                          aria-label="delete"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => toggleFavorite(f, e)}
                        data-testid={`fav-toggle-${uid}`}
                        aria-label={isFav ? t("food.removeFav") : t("food.addToFav")}
                        className="h-9 w-9 rounded-full flex items-center justify-center hover:bg-red-50 shrink-0"
                      >
                        <Heart
                          className={`h-5 w-5 transition ${isFav ? "fill-red-500 text-red-500" : "text-slate-300"}`}
                        />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Barcode scanner overlay */}
        {barcodeOpen && !selectedFood && !customForm && (
          <BarcodeOverlay
            t={t}
            errorMap={errorMap}
            scannerActive={scannerActive}
            scannerError={scannerError}
            scannerId={scannerId}
            barcode={barcode}
            setBarcode={setBarcode}
            onClose={async () => { await stopScanner(); setBarcodeOpen(false); }}
            onStart={startScanner}
            onSubmit={() => barcode && doBarcodeCode(barcode)}
            loading={loading}
            onLabelFile={handleLabelFile}
            labelLoading={labelLoading}
            torchOn={torchOn}
            torchSupported={torchSupported}
            onTorchToggle={toggleTorch}
            zoom={zoom}
            scanHit={scanHit}
            onZoomSet={setZoomValue}
            focusRipple={focusRipple}
            onFocusTap={focusAt}
          />
        )}

        {/* Manual barcode add overlay (shown when EAN is not in OpenFoodFacts) */}
        {manualOpen && (
          <ManualBarcodeOverlay
            t={t}
            form={manualForm}
            setForm={setManualForm}
            saving={manualSaving}
            onCancel={() => setManualOpen(false)}
            onSave={saveManualBarcode}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ManualBarcodeOverlay({ t, form, setForm, saving, onCancel, onSave }) {
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [analyzing, setAnalyzing] = useState(false);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const productPhotoRef = useRef(null);  // camera input for product-photo retake

  const resizeImg = (file, maxSize = 1400, quality = 0.8) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    img.src = url;
  });

  const handleLabelPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    try {
      const dataUrl = await resizeImg(file);
      const { data } = await api.post("/foods/nutrition-label/analyze", { image_base64: dataUrl });
      const p100 = data.per_100g || {};
      setForm((f) => ({
        ...f,
        // Only pre-fill fields that are currently empty (don't overwrite user's edits)
        name: f.name || data.name || "",
        brand: f.brand || data.brand || "",
        calories: f.calories === "" && p100.calories != null ? String(p100.calories) : f.calories,
        protein: f.protein === "" && p100.protein != null ? String(p100.protein) : f.protein,
        carbs: f.carbs === "" && p100.carbs != null ? String(p100.carbs) : f.carbs,
        fat: f.fat === "" && p100.fat != null ? String(p100.fat) : f.fat,
        sugar: f.sugar === "" && p100.sugar != null ? String(p100.sugar) : f.sugar,
        fiber: f.fiber === "" && p100.fiber != null ? String(p100.fiber) : f.fiber,
        saturated_fat: f.saturated_fat === "" && p100.saturated_fat != null ? String(p100.saturated_fat) : f.saturated_fat,
        salt: f.salt === "" && p100.salt != null ? String(p100.salt) : f.salt,
      }));
      if (data.warning) toast.warning(data.warning);
      else toast.success(t("food.manualAdd.labelAnalyzed"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("food.manualAdd.labelError"));
    } finally {
      setAnalyzing(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Product-photo picker — user can retake / attach an image of the *pack*
  // (not the label) when the auto-capture from the scanner missed. Downscale
  // to 640px JPEG so the payload stays under the backend's 500 KB cap.
  const handleProductPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await resizeImg(file, 640, 0.75);
      setForm((f) => ({ ...f, photo_base64: dataUrl }));
    } catch {
      toast.error(t("common.error"));
    } finally {
      if (productPhotoRef.current) productPhotoRef.current.value = "";
    }
  };

  return (
    <div className="absolute inset-0 z-40 bg-white dark:bg-slate-900 flex flex-col" data-testid="manual-barcode-overlay">
      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-[#5A8B8F]/10 text-[#2C5254] flex items-center justify-center shrink-0 font-heading font-bold text-sm">
          +
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base font-semibold text-[#1A1D1E] dark:text-slate-50 leading-tight">
            {t("food.manualAdd.title")}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {t("food.manualAdd.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-2xl leading-none px-1"
          data-testid="manual-barcode-close"
          aria-label="close"
        >×</button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {/* AI photo-label shortcut */}
        <div className="rounded-xl border-2 border-dashed border-[#E85D1C]/40 bg-gradient-to-br from-[#E85D1C]/5 to-[#E8951C]/5 p-3">
          <p className="text-xs font-semibold text-[#E85D1C] flex items-center gap-1.5">
            <span className="text-base">✨</span>
            {t("food.manualAdd.labelAiTitle")}
          </p>
          <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-1 leading-snug">
            {t("food.manualAdd.labelAiSubtitle")}
          </p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <label className="cursor-pointer rounded-lg bg-[#E85D1C] hover:bg-[#d1521a] text-white text-xs font-semibold h-9 flex items-center justify-center gap-1.5" data-testid="manual-barcode-photo-camera">
              📷 {t("food.manualAdd.labelCamera")}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleLabelPhoto} disabled={analyzing} />
            </label>
            <label className="cursor-pointer rounded-lg border border-[#E85D1C]/50 text-[#E85D1C] text-xs font-semibold h-9 flex items-center justify-center gap-1.5 hover:bg-[#E85D1C]/10" data-testid="manual-barcode-photo-upload">
              ⬆ {t("food.manualAdd.labelUpload")}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLabelPhoto} disabled={analyzing} />
            </label>
          </div>
          {analyzing && (
            <p className="text-[11px] text-[#E85D1C] font-semibold mt-2 flex items-center gap-1.5" data-testid="manual-barcode-analyzing">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-[#E85D1C] border-t-transparent animate-spin" />
              {t("food.manualAdd.labelAnalyzing")}
            </p>
          )}
        </div>

        {/* Product-photo preview — auto-populated with the camera frame
            captured the instant the EAN was scanned. Users can retake if
            the auto-shot was blurry, or remove it entirely. Saved to disk
            on the backend so future scans of the same code show a real
            product thumbnail rather than a generic barcode icon. */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3" data-testid="manual-barcode-product-photo">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
            {t("food.manualAdd.productPhoto") || "Foto del prodotto"}
          </p>
          <div className="mt-2 flex items-center gap-3">
            {form.photo_base64 ? (
              <img
                src={form.photo_base64}
                alt="product"
                className="h-16 w-16 rounded-lg object-cover border border-slate-200 dark:border-slate-700"
                data-testid="manual-barcode-product-photo-preview"
              />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-slate-100 dark:bg-slate-900 border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center text-slate-400">
                <Camera className="h-5 w-5" />
              </div>
            )}
            <div className="flex-1 flex flex-col gap-1.5">
              <label className="cursor-pointer rounded-lg bg-[#5A8B8F] hover:bg-[#4a7679] text-white text-xs font-semibold h-8 flex items-center justify-center gap-1.5" data-testid="manual-barcode-product-photo-camera">
                <Camera className="h-3.5 w-3.5" />
                {form.photo_base64
                  ? (t("food.manualAdd.productPhotoRetake") || "Rifai foto")
                  : (t("food.manualAdd.productPhotoAdd") || "Aggiungi foto")}
                <input
                  ref={productPhotoRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleProductPhoto}
                />
              </label>
              {form.photo_base64 && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, photo_base64: null }))}
                  data-testid="manual-barcode-product-photo-remove"
                  className="rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300 text-[11px] font-semibold h-7 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  {t("common.remove") || "Rimuovi"}
                </button>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {t("food.manualAdd.barcode")}
          </label>
          <input
            type="text"
            value={form.barcode}
            onChange={update("barcode")}
            className="mt-1 w-full h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm tabular-nums"
            data-testid="manual-barcode-code"
            inputMode="numeric"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {t("food.manualAdd.name")} *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            placeholder={t("food.manualAdd.namePlaceholder")}
            className="mt-1 w-full h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
            data-testid="manual-barcode-name"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            {t("food.manualAdd.brand")}
          </label>
          <input
            type="text"
            value={form.brand}
            onChange={update("brand")}
            placeholder={t("food.manualAdd.brandPlaceholder")}
            className="mt-1 w-full h-10 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm"
            data-testid="manual-barcode-brand"
          />
        </div>
        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 pt-2">
          {t("food.manualAdd.per100g")}
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { k: "calories", label: t("food.manualAdd.kcal"), unit: "kcal" },
            { k: "protein", label: t("food.manualAdd.protein"), unit: "g" },
            { k: "carbs", label: t("food.manualAdd.carbs"), unit: "g" },
            { k: "fat", label: t("food.manualAdd.fat"), unit: "g" },
            { k: "sugar", label: t("food.manualAdd.sugar"), unit: "g" },
            { k: "fiber", label: t("food.manualAdd.fiber"), unit: "g" },
            { k: "saturated_fat", label: t("food.manualAdd.satFat"), unit: "g" },
            { k: "salt", label: t("food.manualAdd.salt"), unit: "g" },
          ].map(({ k, label, unit }) => (
            <label key={k} className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {label} <span className="text-slate-400">({unit})</span>
              </span>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form[k]}
                onChange={update(k)}
                className="mt-0.5 h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 text-sm tabular-nums"
                data-testid={`manual-barcode-${k}`}
              />
            </label>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 italic leading-relaxed pt-1">
          {t("food.manualAdd.hint")}
        </p>
      </div>

      <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-800 flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 h-11 rounded-full border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold"
          data-testid="manual-barcode-cancel"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !form.barcode.trim() || !form.name.trim()}
          className="flex-1 h-11 rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white text-sm font-semibold disabled:opacity-50"
          data-testid="manual-barcode-save"
        >
          {saving ? t("common.saving") : t("food.manualAdd.saveAndUse")}
        </button>
      </div>
    </div>
  );
}

// ------------ Quantity step (unchanged from previous) ------------
function QuantityStep({ t, selectedFood, quantityG, setQuantityG, scaled, alt, onBack, onConfirm, onRename }) {
  const { redKinds = [], altVisible = false, altState = {}, loadAlternatives, onPick } = alt || {};
  const isRecipe = selectedFood?.source === "recipe";
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const commitName = () => {
    const v = nameDraft.trim();
    setEditingName(false);
    if (v && v !== selectedFood?.name) onRename?.(v);
  };
  // Piece-based portions (½ avocado, 2 eggs, …) for foods with standard weights.
  const pieceW = !isRecipe ? pieceInfoFor(selectedFood?.name) : null;
  // Remember the last piece choice PER food (localStorage) — frequent foods
  // open with the user's usual portion preselected.
  const memKey = `solano.piece.${String(selectedFood?.name || "").trim().toLowerCase()}`;
  const saved = (() => {
    if (!pieceW) return null;
    try { return JSON.parse(localStorage.getItem(memKey) || "null"); } catch { return null; }
  })();
  const [mode, setMode] = useState(saved?.mode === "piece" ? "piece" : "g");
  const [frac, setFrac] = useState(saved?.frac || 1);
  const [size, setSize] = useState(saved?.size || "m"); // s | m | l — medium preselected
  useEffect(() => {
    if (mode === "piece" && pieceW) setQuantityG(Math.max(1, Math.round(frac * pieceW[size])));
  }, [mode, frac, size]);  // eslint-disable-line react-hooks/exhaustive-deps
  const confirmAndRemember = () => {
    if (pieceW) {
      try { localStorage.setItem(memKey, JSON.stringify({ mode, frac, size })); } catch {}
    }
    onConfirm();
  };
  return (
    <div className="flex-1 overflow-y-auto px-5 pb-24 space-y-4 bg-[#E5F0F1] dark:bg-[#0f1416]" data-testid="quantity-step">
      <button
        type="button"
        onClick={onBack}
        data-testid="quantity-back"
        className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white shadow-sm text-sm font-medium text-[#2C5254] hover:shadow active:scale-95 transition"
      >
        <ChevronLeft className="h-4 w-4" /> {t("food.backToResults")}
      </button>

      {/* Selected-food card — for recipes we show the actual dish photo on
          the left thumbnail so the user recognises what they're logging. */}
      <div className="flex items-center gap-3 rounded-3xl bg-white dark:bg-[#1a1d1e] shadow-sm p-3">
        {isRecipe && selectedFood.image_url ? (
          <img
            src={selectedFood.image_url}
            alt={selectedFood.name}
            className="h-14 w-14 rounded-2xl object-cover shrink-0"
            data-testid="quantity-recipe-thumb"
          />
        ) : (
          <div className="h-14 w-14 rounded-2xl bg-[#E5F0F1] dark:bg-[#0f1416] flex items-center justify-center shrink-0">
            <ScanLine className="h-5 w-5 text-[#5A8B8F]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-1.5">
              <Input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitName(); }}
                onBlur={commitName}
                data-testid="quantity-name-input"
                className="h-9 text-sm font-semibold"
              />
              <button onMouseDown={(e) => e.preventDefault()} onClick={commitName} data-testid="quantity-name-save"
                className="h-9 w-9 rounded-full bg-[#2C5254] text-white flex items-center justify-center shrink-0">
                <Check className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { if (!isRecipe) { setNameDraft(selectedFood.name === "—" ? "" : selectedFood.name || ""); setEditingName(true); } }}
              data-testid="quantity-name-edit"
              className="flex items-center gap-1.5 max-w-full text-left"
            >
              <p className="font-semibold text-[#2C5254] dark:text-teal-200 truncate">{selectedFood.name}</p>
              {!isRecipe && <Pencil className="h-3 w-3 text-slate-300 shrink-0" />}
            </button>
          )}
          {selectedFood.brand && <p className="text-xs text-slate-500 truncate">{selectedFood.brand}</p>}
          <p className="text-xs text-slate-500 mt-0.5">
            {selectedFood.per_100g?.calories ? `${Math.round(selectedFood.per_100g.calories)} kcal` : "—"}
            {" · P "}{selectedFood.per_100g?.protein ?? "—"}g
            {" · C "}{selectedFood.per_100g?.carbs ?? "—"}g
            {" · G "}{selectedFood.per_100g?.fat ?? "—"}g
            <span className="text-slate-300"> / 100g</span>
          </p>
        </div>
      </div>

      {/* Recipe details card — servings / prep time chips + ingredients
          list + instructions. Only rendered when the picked food is a
          recipe; collapsible so heavy recipes don't push the quantity
          controls off-screen. */}
      {isRecipe && (
        <div className="rounded-3xl bg-white dark:bg-[#1a1d1e] shadow-sm p-4" data-testid="quantity-recipe-details">
          <button
            type="button"
            onClick={() => setDetailsOpen((v) => !v)}
            className="w-full flex items-center justify-between"
            data-testid="quantity-recipe-toggle"
          >
            <div className="flex items-center gap-2 flex-wrap">
              {selectedFood.servings ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-[#E5F0F1] text-[#2C5254] px-2 py-0.5">
                  <UserSquare2 className="h-3 w-3" />
                  {t("food.servingsN", { n: selectedFood.servings }) || `${selectedFood.servings} ${t("food.servings") || "porzioni"}`}
                </span>
              ) : null}
              {selectedFood.prep_minutes ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-[#E8951C]/15 text-[#E8951C] px-2 py-0.5">
                  <History className="h-3 w-3" />
                  {selectedFood.prep_minutes} min
                </span>
              ) : null}
              {selectedFood.portion_g ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-[#5A8B8F]/15 text-[#5A8B8F] px-2 py-0.5">
                  1 {t("food.portion") || "porzione"} · {selectedFood.portion_g}g
                </span>
              ) : null}
            </div>
            <ChevronLeft className={`h-4 w-4 text-slate-400 transition-transform ${detailsOpen ? "-rotate-90" : "rotate-90"}`} />
          </button>

          {detailsOpen && (
            <div className="mt-3 space-y-3">
              {selectedFood.description && (
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {selectedFood.description}
                </p>
              )}
              {Array.isArray(selectedFood.ingredients) && selectedFood.ingredients.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold mb-1.5">
                    {t("food.recipeIngredients") || "Zutaten"}
                  </p>
                  <ul className="space-y-1" data-testid="recipe-ingredients">
                    {selectedFood.ingredients.map((ing, i) => (
                      <li key={i} className="text-sm text-slate-700 dark:text-slate-200 flex items-start gap-2">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#5A8B8F] shrink-0" aria-hidden />
                        <span className="flex-1">{typeof ing === "string" ? ing : (ing.name || ing.description || JSON.stringify(ing))}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(selectedFood.instructions) && selectedFood.instructions.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold mb-1.5">
                    {t("food.recipeSteps") || "Zubereitung"}
                  </p>
                  <ol className="space-y-2" data-testid="recipe-instructions">
                    {selectedFood.instructions.map((step, i) => (
                      <li key={i} className="text-sm text-slate-700 dark:text-slate-200 flex items-start gap-2">
                        <span className="shrink-0 h-5 w-5 rounded-full bg-[#2C5254] text-white text-[11px] font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="flex-1 pt-0.5">{typeof step === "string" ? step : (step.text || JSON.stringify(step))}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {Array.isArray(selectedFood.tags) && selectedFood.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {selectedFood.tags.slice(0, 6).map((tag, i) => (
                    <span key={i} className="text-[10px] font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quantity card — big input + quick-pick chips */}
      <div className="rounded-3xl bg-white dark:bg-[#1a1d1e] shadow-sm p-4">
        <div className="flex items-center justify-between">
          <label className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold">{t("food.quantityConsumed")}</label>
          {pieceW && (
            <div className="flex rounded-full bg-[#E5F0F1] p-0.5" data-testid="quantity-mode-toggle">
              {[
                { k: "g", lbl: t("food.gramsTab") },
                { k: "piece", lbl: t("food.pieces") },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setMode(o.k)}
                  data-testid={`quantity-mode-${o.k}`}
                  className={`px-3 h-7 rounded-full text-xs font-semibold transition ${
                    mode === o.k ? "bg-[#2C5254] text-white shadow-sm" : "text-[#2C5254]"
                  }`}
                >
                  {o.lbl}
                </button>
              ))}
            </div>
          )}
        </div>
        {mode === "piece" && pieceW ? (
          <div className="mt-3" data-testid="piece-selector">
            <div className="flex flex-wrap gap-2">
              {PIECE_FRACTIONS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFrac(f.value)}
                  data-testid={`piece-frac-${f.value}`}
                  className={`min-w-[44px] px-3 h-10 rounded-full text-sm font-bold transition active:scale-95 ${
                    frac === f.value ? "bg-[#E8951C] text-white shadow-sm" : "bg-[#E5F0F1] text-[#2C5254] hover:bg-[#d5e5e7]"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="mt-2.5 flex gap-2">
              {[
                { k: "s", lbl: t("food.sizeSmall") },
                { k: "m", lbl: t("food.sizeMedium") },
                { k: "l", lbl: t("food.sizeLarge") },
              ].map((o) => (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => setSize(o.k)}
                  data-testid={`piece-size-${o.k}`}
                  className={`flex-1 h-9 rounded-full text-xs font-semibold transition active:scale-95 ${
                    size === o.k ? "bg-[#5A8B8F] text-white shadow-sm" : "bg-[#E5F0F1] text-[#2C5254] hover:bg-[#d5e5e7]"
                  }`}
                >
                  {o.lbl} <span className="opacity-70 font-normal">({pieceW[o.k]}g)</span>
                </button>
              ))}
            </div>
            <p className="mt-2.5 text-center text-sm text-slate-500" data-testid="piece-grams-preview">
              = <b className="text-[#2C5254] font-heading text-lg">{quantityG}g</b>
            </p>
          </div>
        ) : (
          <>
        <div className="mt-2 flex items-center gap-2">
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            max={2000}
            value={quantityG}
            onChange={(e) => setQuantityG(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmAndRemember(); }}
            data-testid="quantity-input"
            className="h-14 text-3xl font-heading font-semibold text-center flex-1 rounded-2xl bg-[#E5F0F1]/60 dark:bg-[#0f1416] border-0 focus-visible:ring-2 focus-visible:ring-[#5A8B8F] text-[#2C5254]"
            autoFocus
          />
          <span className="text-lg font-heading font-semibold text-[#5A8B8F] pr-2">{unitFor(selectedFood?.name)}</span>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {[30, 50, 100, 150, 200, 250].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setQuantityG(v)}
              data-testid={`quantity-quick-${v}`}
              className={`px-3 h-8 rounded-full text-xs font-semibold transition active:scale-95 ${
                +quantityG === v
                  ? "bg-[#2C5254] text-white shadow-sm"
                  : "bg-[#E5F0F1] text-[#2C5254] hover:bg-[#d5e5e7]"
              }`}
            >
              {v}g
            </button>
          ))}
        </div>
          </>
        )}
      </div>

      {/* Preview card — matches AddMeal macro summary: colored macro dots
          and font-heading kcal, so the scaled portion feels like the same
          artefact you'll see once saved. */}
      <div className="rounded-3xl bg-white dark:bg-[#1a1d1e] shadow-sm p-4" data-testid="quantity-preview">
        <p className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold mb-2">{t("food.yourPortion")}</p>
        <div className="flex items-baseline gap-2 mb-3">
          <span className="font-heading text-4xl font-bold text-[#E8951C]" data-testid="quantity-preview-kcal">
            {scaled?.kcal ?? 0}
          </span>
          <span className="text-sm font-medium text-[#E8951C]">kcal</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="font-heading text-base font-semibold text-[#1A1D1E] dark:text-white" data-testid="quantity-preview-carbs">
              {scaled?.carbs ?? 0}<span className="text-xs text-slate-400">g</span>
            </p>
            <div className="h-1 w-6 mx-auto bg-purple-400 rounded-full my-1" />
            <p className="text-[10px] text-purple-500 uppercase tracking-wider font-medium">{t("cliDash.carbs")}</p>
          </div>
          <div>
            <p className="font-heading text-base font-semibold text-[#1A1D1E] dark:text-white" data-testid="quantity-preview-protein">
              {scaled?.protein ?? 0}<span className="text-xs text-slate-400">g</span>
            </p>
            <div className="h-1 w-6 mx-auto bg-[#5A8B8F] rounded-full my-1" />
            <p className="text-[10px] text-[#2C5254] uppercase tracking-wider font-medium">{t("cliDash.protein")}</p>
          </div>
          <div>
            <p className="font-heading text-base font-semibold text-[#1A1D1E] dark:text-white" data-testid="quantity-preview-fat">
              {scaled?.fat ?? 0}<span className="text-xs text-slate-400">g</span>
            </p>
            <div className="h-1 w-6 mx-auto bg-lime-500 rounded-full my-1" />
            <p className="text-[10px] text-lime-700 uppercase tracking-wider font-medium">{t("cliDash.fat")}</p>
          </div>
        </div>
        {/* Secondary nutrients — sugar, fibre, saturated fat, salt (scaled to the portion) */}
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-white/5" data-testid="quantity-preview-details">
          {[scaled?.sugar, scaled?.fiber, scaled?.saturated_fat, scaled?.salt].some((v) => v != null) ? (
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { key: "sugar", kind: "sugar", label: t("food.sugar"), value: scaled?.sugar },
                { key: "fiber", label: t("food.fiber"), value: scaled?.fiber },
                { key: "satFat", kind: "saturated_fat", label: t("food.satFat"), value: scaled?.saturated_fat },
                { key: "salt", kind: "salt", label: t("food.salt"), value: scaled?.salt },
              ].map((d) => {
                const light = d.kind ? trafficLight(d.kind, selectedFood?.per_100g?.[d.kind], unitFor(selectedFood?.name) === "ml") : null;
                const st = light ? LIGHT_STYLES[light] : null;
                return (
                  <div
                    key={d.key}
                    className={`relative rounded-2xl px-1.5 py-2 text-center ring-1 ${st ? `${st.bg} ${st.ring}` : "bg-slate-50 dark:bg-white/5 ring-transparent"}`}
                    data-testid={`quantity-preview-${d.key}`} data-light={light || undefined}
                  >
                    {st && <span className={`absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full ${st.dot}`} />}
                    <p className={`font-heading text-sm font-semibold tabular-nums ${d.value != null ? "text-[#1A1D1E] dark:text-white" : "text-slate-300 dark:text-slate-600"}`}>
                      {d.value != null ? d.value : "–"}<span className="text-[10px] text-slate-400 ml-0.5">g</span>
                    </p>
                    <p className={`text-[9px] uppercase tracking-wider font-semibold leading-tight mt-0.5 ${st ? st.text : "text-slate-500 dark:text-slate-400"}`}>{d.label}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center" data-testid="quantity-preview-nodetails">{t("food.noDetails")}</p>
          )}
          {redKinds.length > 0 && (
            <div className="mt-3" data-testid="healthier-alternatives">
              {!altVisible ? (
                <button
                  type="button" onClick={loadAlternatives} data-testid="healthier-alternatives-btn"
                  className="w-full flex items-center justify-between gap-2 rounded-2xl bg-emerald-50 dark:bg-emerald-900/25 ring-1 ring-emerald-200/70 dark:ring-emerald-700/40 px-3.5 py-2.5 text-left active:scale-[0.99] transition"
                >
                  <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">{t("food.altTitle")}</span>
                  <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-0.5">{t("food.altCta")}<ChevronRight className="h-3 w-3" /></span>
                </button>
              ) : (
                <div className="rounded-2xl bg-emerald-50/70 dark:bg-emerald-900/20 ring-1 ring-emerald-200/60 dark:ring-emerald-700/30 p-2.5">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300 px-1 mb-1.5">{t("food.altTitle")}</p>
                  {altState.loading && <p className="text-xs text-slate-400 px-1 py-2">…</p>}
                  {!altState.loading && altState.items?.length === 0 && <p className="text-xs text-slate-500 px-1 py-1" data-testid="healthier-alternatives-empty">{t("food.altEmpty")}</p>}
                  {altState.items?.map((a) => (
                    <button
                      key={a.id} type="button" onClick={() => onPick(a)}
                      data-testid={`healthier-alt-${a.id}`}
                      className="w-full flex items-center gap-2 rounded-xl bg-white dark:bg-[#1a1d1e] px-3 py-2 mb-1.5 last:mb-0 text-left shadow-sm active:scale-[0.99] transition"
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-[#1A1D1E] dark:text-white truncate">{a.name}</span>
                        <span className="block text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {a.per_100g?.calories != null ? `${Math.round(a.per_100g.calories)} kcal/100g` : ""}{a.why ? ` · ${a.why}` : ""}
                        </span>
                      </span>
                      <NutrientLights per100={a.per_100g} name={a.name} uid={`alt-${a.id}`} labels={{ sugar: t("food.lightSugarShort"), saturated_fat: t("food.lightFatShort"), salt: t("food.lightSaltShort") }} />
                      <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {[selectedFood?.per_100g?.sugar, selectedFood?.per_100g?.saturated_fat, selectedFood?.per_100g?.salt].some((v) => v != null) && (
            <p className="mt-2 flex items-center justify-center gap-2 text-[10px] text-slate-400 dark:text-slate-500" data-testid="quantity-preview-legend">
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{t("food.lightLow")}</span>
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-amber-400" />{t("food.lightMid")}</span>
              <span className="inline-flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" />{t("food.lightHigh")}</span>
              <span>· {t("food.lightBasis")}</span>
            </p>
          )}
        </div>
      </div>

      {/* Sticky confirm button — teal pill with amber wheat, mirroring
          the AddMeal Speichern button so the whole flow feels of one piece. */}
      <div className="fixed bottom-0 inset-x-0 pt-3 px-5 bg-gradient-to-t from-[#E5F0F1] via-[#E5F0F1] to-transparent pointer-events-none" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)" }}>
        <div className="max-w-2xl mx-auto relative pointer-events-auto">
          <Button
            onClick={confirmAndRemember}
            data-testid="quantity-confirm-btn"
            className="w-full h-14 rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white text-base font-semibold shadow-lg gap-2 pr-12"
          >
            <Check className="h-5 w-5" /> {t("food.addToMeal")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ------------ Custom-food create/edit form ------------
function CustomForm({ t, form, setForm, onSave, onCancel }) {
  const setPer = (k, v) => setForm({ ...form, per_100g: { ...form.per_100g, [k]: v } });
  return (
    <div className="flex-1 overflow-y-auto px-5 pb-24 space-y-4 bg-[#E5F0F1] dark:bg-[#0f1416]" data-testid="custom-form">
      <button
        type="button"
        onClick={onCancel}
        className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white shadow-sm text-sm font-medium text-[#2C5254] hover:shadow active:scale-95 transition"
        data-testid="custom-cancel"
      >
        <ChevronLeft className="h-4 w-4" /> {t("food.cancel")}
      </button>

      <p className="font-heading text-xl font-semibold text-[#2C5254] dark:text-teal-200">
        {form.editing_id ? t("food.editCustom") : t("food.createCustom")}
      </p>

      {/* Basic info card */}
      <div className="rounded-3xl bg-white dark:bg-[#1a1d1e] shadow-sm p-4 space-y-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold">{t("food.customName")} *</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="custom-name"
            className="h-11 mt-1 rounded-2xl bg-[#E5F0F1]/60 dark:bg-[#0f1416] border-0 focus-visible:ring-2 focus-visible:ring-[#5A8B8F]" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold">{t("food.customBrand")}</label>
            <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="custom-brand"
              className="h-11 mt-1 rounded-2xl bg-[#E5F0F1]/60 dark:bg-[#0f1416] border-0 focus-visible:ring-2 focus-visible:ring-[#5A8B8F]" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold">{t("food.customNotes")}</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="custom-notes"
              className="h-11 mt-1 rounded-2xl bg-[#E5F0F1]/60 dark:bg-[#0f1416] border-0 focus-visible:ring-2 focus-visible:ring-[#5A8B8F]" />
          </div>
        </div>
      </div>

      {/* Per-100g macros card — colored dot per macro so users read the
          form at a glance (mirrors the AddMeal macro palette). */}
      <div className="rounded-3xl bg-white dark:bg-[#1a1d1e] shadow-sm p-4">
        <p className="font-heading text-sm font-semibold text-[#2C5254] dark:text-teal-200 mb-3">
          {t("food.per100g")}
        </p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-3">
          {[
            ["calories",       "food.customKcal",    "bg-[#E8951C]"],  // amber flame
            ["protein",        "food.customProtein", "bg-[#2C5254]"],  // teal dumbbell
            ["carbs",          "food.customCarbs",   "bg-purple-500"], // purple wheat
            ["fat",            "food.customFat",     "bg-lime-500"],   // green droplet
            ["sugar",          "food.customSugar",   "bg-pink-400"],
            ["fiber",          "food.customFiber",   "bg-emerald-500"],
            ["saturated_fat",  "food.customSat",     "bg-orange-400"],
            ["salt",           "food.customSalt",    "bg-sky-400"],
          ].map(([k, i18nKey, dot]) => (
            <div key={k}>
              <label className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${dot}`} aria-hidden />
                {t(i18nKey)}
              </label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.1"
                value={form.per_100g[k] ?? ""}
                onChange={(e) => setPer(k, e.target.value)}
                data-testid={`custom-per-${k}`}
                className="h-10 mt-1 rounded-2xl bg-[#E5F0F1]/60 dark:bg-[#0f1416] border-0 focus-visible:ring-2 focus-visible:ring-[#5A8B8F]"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Sticky save button — teal pill with amber wheat ornament,
          matching the AddMeal Speichern button. */}
      <div className="fixed bottom-0 inset-x-0 pt-3 px-5 bg-gradient-to-t from-[#E5F0F1] via-[#E5F0F1] to-transparent pointer-events-none" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1.25rem)" }}>
        <div className="max-w-2xl mx-auto relative pointer-events-auto">
          <Button
            onClick={onSave}
            data-testid="custom-save"
            className="w-full h-14 rounded-full bg-[#5A8B8F] hover:bg-[#4a7679] text-white text-base font-semibold shadow-lg gap-2"
          >
            <Check className="h-5 w-5" /> {t("food.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ------------ Barcode overlay ------------
function BarcodeOverlay({
  t, errorMap, scannerActive, scannerError, scannerId,
  barcode, setBarcode, onClose, onStart, onSubmit, loading,
  onLabelFile, labelLoading,
  torchOn, torchSupported, onTorchToggle,
  zoom, onZoomSet, scanHit,
  focusRipple, onFocusTap,
}) {
  const labelCameraRef = useRef(null);
  const labelFileRef = useRef(null);
  usePinchZoom(() => document.getElementById(scannerId), zoom, onZoomSet, scannerActive);
  return (
    <div className="absolute inset-0 bg-[#E5F0F1] dark:bg-[#0f1416] z-20 flex flex-col" data-testid="barcode-overlay">
      <div className="px-5 pt-5 pb-4 flex items-center justify-between">
        <button
          onClick={onClose}
          data-testid="barcode-close"
          className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-white shadow-sm text-sm font-medium text-[#2C5254] hover:shadow active:scale-95 transition"
        >
          <ChevronLeft className="h-4 w-4" /> {t("food.backToResults")}
        </button>
        <p className="font-heading text-base font-semibold text-[#2C5254] dark:text-teal-200">{t("food.byBarcode")}</p>
        <div className="w-9" />
      </div>

      <div className="flex-1 px-5 pb-6 space-y-3 overflow-y-auto">
        {scannerActive ? (
          <div className="relative h-full flex flex-col">
            <div id={scannerId}
              onClick={onFocusTap}
              onTouchStart={onFocusTap}
              className="flex-1 rounded-3xl overflow-hidden bg-slate-900 shadow-lg cursor-crosshair touch-none [&_video]:w-full [&_video]:h-full [&_video]:rounded-3xl [&_video]:block [&_video]:object-cover"
              style={{ minHeight: 320 }}
              data-testid="scanner-region" />
            {/* Tap-to-focus ripple — a small crosshair that pulses at the
                tap location for ~700 ms so the user gets immediate feedback,
                independent of whether the hardware focus actually shifts. */}
            {focusRipple && (
              <div
                key={focusRipple.key}
                aria-hidden
                className="absolute pointer-events-none"
                style={{
                  left: `calc(${focusRipple.x}% - 24px)`,
                  top:  `calc(${focusRipple.y}% - 24px)`,
                  width: 48, height: 48,
                  border: "2px solid rgba(255, 220, 130, 0.95)",
                  borderRadius: 12,
                  boxShadow: "0 0 0 4px rgba(255, 220, 130, 0.25)",
                  animation: "focusRipplePulse 0.7s ease-out forwards",
                }}
              />
            )}
            <ScanSuccessFlash show={scanHit} testId="scanner-success-flash" />
            {/* Torch / flashlight toggle — only shown when the browser
                exposes the "torch" MediaTrack capability (mostly Android
                Chrome). Floats over the top-right of the camera stream. */}
            {torchSupported && (
              <button
                type="button"
                onClick={onTorchToggle}
                data-testid="scanner-torch"
                aria-pressed={torchOn}
                aria-label={t("food.torch") || "Torch"}
                className={`absolute top-3 right-3 h-11 w-11 rounded-full flex items-center justify-center shadow-lg active:scale-95 transition backdrop-blur ${
                  torchOn ? "bg-amber-400 text-slate-900" : "bg-slate-900/60 text-white"
                }`}
              >
                {torchOn ? <Zap className="h-5 w-5" /> : <ZapOff className="h-5 w-5" />}
              </button>
            )}
            {/* Zoom slider — only when the camera exposes a `zoom` capability. */}
            <ScannerZoomControl zoom={zoom} onChange={onZoomSet} label={t("food.zoom") || "Zoom"} testId="scanner-zoom" className="mt-2 shrink-0" />
            <p className="text-xs text-slate-500 text-center mt-3 shrink-0">
              {t("food.aimAtBarcode")}
              <span className="block mt-1 text-slate-400/90">
                {t("food.scannerFocusHint") || "Se non legge, tocca il codice per mettere a fuoco."}{zoom ? ` ${t("food.pinchZoomHint")}` : ""}
              </span>
            </p>
          </div>
        ) : (
          <>
            <Button onClick={onStart} data-testid="scanner-start"
              className="w-full rounded-2xl bg-[#5A8B8F] hover:bg-[#4a7679] text-white gap-2 h-12 shadow-sm text-sm font-semibold">
              <Camera className="h-4 w-4" /> {t("food.scanWithCamera")}
            </Button>
            {scannerError && (
              <div className="rounded-2xl bg-red-50 border border-red-200 px-3 py-2.5 text-xs text-red-700" data-testid="scanner-error">
                <p className="font-medium">{t(errorMap[scannerError.code]?.i18n || "food.camError")}</p>
                {errorMap[scannerError.code]?.withTip && (
                  <p className="mt-1 text-red-600/80">{t(IS_NATIVE ? "food.camTipNative" : "food.camTip")}</p>
                )}
              </div>
            )}
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-slate-300/60" />
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{t("food.or")}</p>
              <div className="flex-1 h-px bg-slate-300/60" />
            </div>
            <div className="rounded-2xl bg-white dark:bg-[#1a1d1e] shadow-sm p-3">
              <label className="text-[11px] uppercase tracking-wider text-[#5A8B8F] font-semibold">EAN / GTIN</label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  data-testid="food-barcode-input"
                  placeholder="8001505005707"
                  inputMode="numeric"
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && onSubmit()}
                  className="h-11 font-mono rounded-2xl bg-[#E5F0F1]/60 dark:bg-[#0f1416] border-0 focus-visible:ring-2 focus-visible:ring-[#5A8B8F]"
                />
                <Button onClick={onSubmit} disabled={loading || !barcode} data-testid="food-barcode-btn"
                  className="rounded-2xl bg-[#2C5254] hover:bg-[#1e3a3c] text-white h-11 px-4 shadow-sm">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanLine className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Nutrition-label OCR fallback — for damaged / missing barcodes.
                The user photographs the "Nährwerte / Valori nutrizionali"
                table on the back of the pack and the AI reads the values. */}
            {onLabelFile && (
              <>
                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-slate-300/60" />
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{t("food.or")}</p>
                  <div className="flex-1 h-px bg-slate-300/60" />
                </div>
                <div className="rounded-2xl border-2 border-dashed border-[#E8951C]/60 bg-gradient-to-br from-[#E8951C]/10 to-[#E8951C]/5 p-4 space-y-2.5">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-2xl bg-[#E8951C]/20 flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4 text-[#E8951C]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#2C5254] dark:text-teal-200 leading-tight">
                        {t("food.labelOcrTitle") || "Barcode illeggibile?"}
                      </p>
                      <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-snug">
                        {t("food.labelOcrBody") || "Scatta una foto della tabella nutrizionale"}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => labelCameraRef.current?.click()}
                      disabled={labelLoading}
                      data-testid="label-ocr-camera"
                      className="flex-1 h-10 rounded-2xl bg-[#E8951C] hover:bg-[#c97e11] text-white gap-2 text-xs font-semibold shadow-sm"
                    >
                      {labelLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                      {t("food.labelOcrCameraCta") || "Scatta"}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => labelFileRef.current?.click()}
                      disabled={labelLoading}
                      data-testid="label-ocr-file"
                      variant="outline"
                      className="flex-1 h-10 rounded-2xl border-[#E8951C]/50 text-[#E8951C] hover:bg-[#E8951C]/10 gap-2 text-xs font-semibold"
                    >
                      {t("food.labelOcrGalleryCta") || "Galleria"}
                    </Button>
                  </div>
                  <input
                    ref={labelCameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => { onLabelFile(e.target.files?.[0]); e.target.value = ""; }}
                  />
                  <input
                    ref={labelFileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { onLabelFile(e.target.files?.[0]); e.target.value = ""; }}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
